/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React, { MutableRefObject } from "react";
import Icon from '../../basic/Icon';
import { CheckIcon } from "@radix-ui/react-icons";
import { useFormStoreFieldValue, useFormStoreSetters } from "../FormStore";
import { cn } from "@/shared/ui/utils/tailwind";

export type CheckboxProps = {
  name?: string;
  checkboxRef?: MutableRefObject<HTMLElement | undefined>;
  error?: boolean;
  label?: React.ReactNode;
  description?: React.ReactNode;
  defaultChecked?: boolean;
  disabled?: boolean;
  required?: boolean;
  isSelected?: boolean;
};

/**
 * Form-connected checkbox.
 *
 * @param {CheckboxProps} props - Checkbox properties.
 * @returns {JSX.Element} Rendered checkbox.
 */
const Checkbox: React.FC<CheckboxProps> = ({ name, checkboxRef, error, ...props }) => {
  const value = Boolean(useFormStoreFieldValue(name ?? ''));
  const { setFieldValue } = useFormStoreSetters();

  return (
    <CheckboxRaw
      {...{ name, checkboxRef, value, error }}
      onToggle={(v) => name && setFieldValue(name, v)}
      {...props}
    />
  );
};

/**
 * Standalone checkbox with toggle callback.
 *
 * @param {CheckboxProps & { value: boolean; onToggle?: (value: boolean) => void }} props - Raw checkbox properties.
 * @returns {JSX.Element} Rendered raw checkbox.
 */
export const CheckboxRaw: React.FC<
  CheckboxProps & {
    value: boolean;
    onToggle?: (value: boolean) => void;
  }
> = ({ name, value, checkboxRef, onToggle, error, label, description, defaultChecked, required, isSelected, ...props }) => {
  void label; void description; void defaultChecked; void required; void isSelected; void props;
  return (
    <button
      type="button"
      ref={checkboxRef as any}
      className={cn(
        "flex items-center justify-center border-2 border-[var(--border-medium)] w-5 h-5 rounded-(--radius-sm)",
        "transition-all hover:shadow-[0_0_0_3px_var(--border-subtle)] hover:cursor-pointer",
        "focus:border-(--seed-base) focus:outline-none",
        value && "border-none bg-(--seed-base) text-white animate-pop",
        error && !value && "border-(--seed-base) shadow-[0_0_0_1px_var(--seed-base)]"
      )}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onToggle?.(!value);
      }}
    >
      {value && <Icon as={CheckIcon} />}
    </button>
  );
};

export default Checkbox;
